import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import PolicyPage, { PolicyContact } from "./PolicyPage";

const policyGroups = [
  {
    title: "Shopping and orders",
    links: [
      ["Orders and cancellations", "/orders-cancellations"],
      ["Payments and security", "/payments-security"],
      ["Shipping and delivery", "/shipping"],
      ["Returns and refunds", "/returns"],
      ["Product care and quality", "/product-care"],
      ["Pricing and promotions", "/promotions"],
      ["Consumer rights", "/consumer-rights"],
    ],
  },
  {
    title: "Privacy and access",
    links: [
      ["Privacy policy", "/privacy"],
      ["Privacy choices", "/privacy-choices"],
      ["Cookie policy", "/cookie-policy"],
      ["Accessibility statement", "/accessibility"],
      ["Fraud prevention", "/fraud-prevention"],
    ],
  },
  {
    title: "Website rules and notices",
    links: [
      ["Terms and conditions", "/terms"],
      ["Acceptable use policy", "/acceptable-use"],
      ["Intellectual property notice", "/intellectual-property"],
      ["Electronic communications", "/electronic-communications"],
      ["Legal notice and contact", "/legal-notice"],
    ],
  },
];

export function LegalHub() {
  return (
    <PolicyPage
      eyebrow="Policies"
      title="Legal and policy centre"
      introduction={`Find the terms, notices, and customer policies that apply when you browse or shop with ${siteConfig.brandName}.`}
      showDate={false}
      sections={policyGroups.map((group, groupIndex) => ({
        id: `policy-group-${groupIndex + 1}`,
        title: group.title,
        content: (
          <ul className="privacy-page__link-list">
            {group.links.map(([label, path]) => (
              <li key={path}>
                <Link to={path}>{label}</Link>
              </li>
            ))}
          </ul>
        ),
      }))}
    />
  );
}

export function PaymentsSecurity() {
  return (
    <PolicyPage
      eyebrow="Client services"
      title="Payments and security"
      introduction="How payment authorization, currency, security checks, and refunds are handled when you place an order."
      sections={[
        {
          id: "payment-methods",
          title: "Accepted payment methods",
          content: (
            <>
              <p>
                The payment methods currently available are displayed at checkout
                and may include Visa, Mastercard, American Express, Apple Pay, and
                Google Pay. Availability can depend on your device, location, order
                value, and the payment provider.
              </p>
              <p>
                We do not accept cash, checks, bank transfers, or payment details
                sent by email unless a checkout page expressly offers that option.
              </p>
            </>
          ),
        },
        {
          id: "payment-authorization",
          title: "Authorization and capture",
          content: (
            <>
              <p>
                Submitting an order authorizes our payment provider to validate the
                payment method and reserve or collect the amount shown in the final
                order summary. A pending authorization is not necessarily a
                completed charge.
              </p>
              <p>
                If an order is declined or canceled, a temporary authorization may
                remain visible until your financial institution releases it. Release
                timing is controlled by that institution.
              </p>
            </>
          ),
        },
        {
          id: "payment-security",
          title: "Payment security",
          content: (
            <>
              <p>
                Card and wallet details are entered on or transmitted to an
                independent payment provider. {siteConfig.brandName} receives the
                payment status and limited identifiers needed to match the payment
                to your order, rather than your complete card number.
              </p>
              <p>
                Your issuer may request an additional authentication step. Never
                send a full card number, security code, password, or one-time code
                to our customer-service email.
              </p>
            </>
          ),
        },
        {
          id: "payment-currency",
          title: "Currency, tax, and bank charges",
          content: (
            <p>
              The checkout total is shown in the selected currency before you pay.
              Your bank may apply conversion, international transaction, or other
              charges under its own terms. Taxes, duties, and delivery charges are
              disclosed in the order summary when applicable.
            </p>
          ),
        },
        {
          id: "payment-screening",
          title: "Verification and fraud screening",
          content: (
            <p>
              We and our payment provider may perform proportionate verification
              and fraud checks. We may request confirmation of order information,
              place an order on hold, or decline a transaction where there is a
              reasonable security concern. See our <Link to="/fraud-prevention">Fraud Prevention Policy</Link>.
            </p>
          ),
        },
        {
          id: "payment-errors",
          title: "Failed, duplicate, or disputed payments",
          content: (
            <>
              <p>
                If checkout fails, check that your billing information matches the
                issuer's records before trying again. Contact us before repeating a
                transaction that appears to have completed.
              </p>
              <p>
                If you do not recognize a payment, contact your issuer promptly and
                email us with the order number. This does not limit any chargeback
                or payment rights available under applicable law or network rules.
              </p>
              <PolicyContact label="Payment enquiries" />
            </>
          ),
        },
      ]}
    />
  );
}

export function OrdersCancellations() {
  return (
    <PolicyPage
      eyebrow="Client services"
      title="Orders and cancellations"
      introduction="Information about placing an order, acceptance, changes, quantity limits, and cancellation requests."
      sections={[
        {
          id: "orders-submit",
          title: "Placing an order",
          content: (
            <p>
              Review product, size, color, delivery, contact, and payment details
              before selecting the final order button. Items in a shopping bag or
              wish list are not reserved, and availability may change before the
              order is accepted.
            </p>
          ),
        },
        {
          id: "orders-acceptance",
          title: "Acknowledgement and acceptance",
          content: (
            <>
              <p>
                The first email is an acknowledgement that we received your order;
                it is not an acceptance unless it expressly says so. A purchase
                contract is formed when we confirm shipment, subject to mandatory
                local law.
              </p>
              <p>
                Keep your order number and confirmation emails. Contact us promptly
                if you do not receive an acknowledgement after successful payment.
              </p>
            </>
          ),
        },
        {
          id: "orders-availability",
          title: "Availability and quantity limits",
          content: (
            <p>
              All orders are subject to stock availability. We may apply reasonable
              per-customer or per-order limits, particularly for limited products,
              and may reject orders that appear to be placed for unauthorized
              resale or through automated purchasing tools.
            </p>
          ),
        },
        {
          id: "orders-changes",
          title: "Changes and customer cancellation requests",
          content: (
            <>
              <p>
                Email us as soon as possible if you need to request a change or
                cancellation. We cannot guarantee a request once payment has been
                processed or preparation has started.
              </p>
              <p>
                If an order can no longer be stopped, receive it and use the
                applicable <Link to="/returns">return process</Link>. Any statutory
                cancellation right remains unaffected.
              </p>
            </>
          ),
        },
        {
          id: "orders-address",
          title: "Delivery details",
          content: (
            <p>
              You are responsible for providing a complete and accurate delivery
              address. For fraud and carrier-security reasons, address changes may
              not be possible after submission. We will not redirect an order to a
              different country or market.
            </p>
          ),
        },
        {
          id: "orders-cancelled-by-us",
          title: "When we may cancel",
          content: (
            <>
              <p>We may decline or cancel an order where:</p>
              <ul>
                <li>An item is unavailable or subject to an obvious pricing or description error.</li>
                <li>Payment cannot be authorized or material fraud risk is identified.</li>
                <li>Delivery details are invalid, incomplete, or outside the supported area.</li>
                <li>Quantity limits, sanctions, export rules, or applicable law prevent fulfilment.</li>
              </ul>
              <p>
                If funds were collected for a canceled item, the corresponding
                amount will be returned to the original payment method.
              </p>
            </>
          ),
        },
        {
          id: "orders-support",
          title: "Order support",
          content: (
            <>
              <p>
                Include the order number and the email address used at checkout in
                your message. Do not include complete payment-card credentials.
              </p>
              <PolicyContact label="Order enquiries" />
            </>
          ),
        },
      ]}
    />
  );
}

export function ProductCare() {
  return (
    <PolicyPage
      eyebrow="Client services"
      title="Product care and quality"
      introduction="Guidance for caring for your purchase and reporting an item that is damaged, incorrect, or not of satisfactory quality."
      sections={[
        {
          id: "care-information",
          title: "Product information and natural variation",
          content: (
            <p>
              Follow the composition, measurement, and care information on the
              product page and item label. Natural leather, fibers, dyes, and
              handcrafted finishes can show small variations in color, grain, or
              texture. These characteristics are not automatically defects.
            </p>
          ),
        },
        {
          id: "care-instructions",
          title: "Care instructions",
          content: (
            <>
              <ul>
                <li>Follow the sewn-in care label and use a qualified specialist where directed.</li>
                <li>Keep pieces away from excessive heat, humidity, direct light, and harsh chemicals.</li>
                <li>Store leather goods filled lightly and inside a breathable protective bag.</li>
                <li>Test any care product on an inconspicuous area before wider application.</li>
              </ul>
              <p>
                Care advice is general and does not replace the instructions
                supplied with a specific item.
              </p>
            </>
          ),
        },
        {
          id: "care-delivery-issue",
          title: "Damage or error on delivery",
          content: (
            <p>
              If an item arrives damaged, incomplete, or different from what you
              ordered, contact us promptly. Keep the item, packaging, labels, and
              delivery materials, and provide the order number plus clear photos so
              we can review the issue.
            </p>
          ),
        },
        {
          id: "care-quality-claim",
          title: "Quality concerns after use",
          content: (
            <p>
              Describe the concern, when it appeared, how the item was used and
              cared for, and include photographs. We may request the item for
              assessment before offering a remedy. Reasonable inspection does not
              reduce any mandatory legal rights.
            </p>
          ),
        },
        {
          id: "care-not-covered",
          title: "Wear, misuse, and alterations",
          content: (
            <p>
              Unless applicable law requires otherwise, remedies do not cover
              ordinary wear, accidental damage, misuse, failure to follow care
              instructions, improper cleaning, or work performed by an unauthorized
              repairer or alteration service.
            </p>
          ),
        },
        {
          id: "care-statutory-rights",
          title: "Statutory rights",
          content: (
            <p>
              Goods remain subject to non-excludable consumer guarantees and legal
              remedies that apply in your location. This policy does not create a
              separate lifetime guarantee and does not restrict those rights. See
              our <Link to="/consumer-rights">Consumer Rights Notice</Link>.
            </p>
          ),
        },
        {
          id: "care-contact",
          title: "Request an assessment",
          content: <PolicyContact label="Product care and quality enquiries" />,
        },
      ]}
    />
  );
}

export function Promotions() {
  return (
    <PolicyPage
      eyebrow="Shopping policies"
      title="Pricing and promotions"
      introduction="General conditions for displayed prices, sale items, promotional codes, and limited offers."
      sections={[
        {
          id: "promo-prices",
          title: "Displayed prices",
          content: (
            <p>
              Prices are shown in the selected market currency. Applicable taxes,
              delivery fees, duties, and other charges are displayed before order
              submission where they can be calculated. Prices may differ between
              markets or sales channels because of local costs and taxes.
            </p>
          ),
        },
        {
          id: "promo-errors",
          title: "Pricing and description errors",
          content: (
            <p>
              We may correct an obvious pricing, discount, availability, or product
              description error before accepting an order. If an affected order has
              already been submitted, we will contact you or cancel and refund it
              where appropriate.
            </p>
          ),
        },
        {
          id: "promo-general",
          title: "General promotion conditions",
          content: (
            <ul>
              <li>Offers apply only during the stated period and while eligible stock lasts.</li>
              <li>Product, market, customer, minimum-spend, and maximum-discount restrictions may apply.</li>
              <li>Unless stated otherwise, promotions cannot be combined or applied after checkout.</li>
              <li>Delivery charges, taxes, duties, prior orders, and excluded products do not count toward a threshold.</li>
            </ul>
          ),
        },
        {
          id: "promo-codes",
          title: "Promotional codes",
          content: (
            <p>
              A code must be entered correctly before order submission and may be
              limited to one use per customer, household, email, account, payment
              method, or order. Codes have no cash value and may not be sold,
              transferred, reproduced, or obtained through unauthorized channels.
            </p>
          ),
        },
        {
          id: "promo-returns",
          title: "Returns from promotional orders",
          content: (
            <p>
              Refunds reflect the amount actually paid for the returned item. If a
              return means an order no longer satisfies a multi-item or
              minimum-spend offer, the refund may be recalculated as allowed by the
              disclosed offer terms and applicable law.
            </p>
          ),
        },
        {
          id: "promo-abuse",
          title: "Fair use and changes",
          content: (
            <p>
              We may reject use involving automation, resale, duplicate identities,
              manipulation, or other abuse. We may change or withdraw an offer
              prospectively, but will not remove rights from an order already
              accepted except where law permits.
            </p>
          ),
        },
        {
          id: "promo-specific",
          title: "Offer-specific terms",
          content: (
            <>
              <p>
                If an advertisement or offer page includes specific conditions,
                those conditions supplement this policy and control if there is a
                conflict about that offer.
              </p>
              <PolicyContact label="Pricing and promotion enquiries" />
            </>
          ),
        },
      ]}
    />
  );
}

export function PrivacyChoices() {
  return (
    <PolicyPage
      title="Privacy choices"
      introduction="Ways to manage communications, browser storage, and personal-information requests. Rights vary by location and are subject to lawful exceptions."
      sections={[
        {
          id: "choice-rights",
          title: "Available privacy rights",
          content: (
            <p>
              Depending on applicable law, you may ask to access, correct, delete,
              restrict, or receive a copy of personal information, or object to
              certain processing. You may also withdraw consent for future
              processing where consent is the legal basis.
            </p>
          ),
        },
        {
          id: "choice-request",
          title: "Submitting a request",
          content: (
            <p>
              Email {siteConfig.contact.email} with the subject "Privacy Request"
              and describe the right you wish to exercise. Provide only the
              information reasonably needed to locate your records. Do not send a
              password or full payment-card number.
            </p>
          ),
        },
        {
          id: "choice-verification",
          title: "Identity verification",
          content: (
            <p>
              We may verify that a request relates to you before disclosing or
              changing information. Verification can involve confirming control of
              the email used for an order and matching limited transaction details.
              We may decline a request we cannot safely verify.
            </p>
          ),
        },
        {
          id: "choice-agents",
          title: "Authorized representatives",
          content: (
            <p>
              Where local law permits, an authorized representative may submit a
              request for you. We may ask for signed authority and may still verify
              your identity directly. Parents or guardians may contact us about a
              child's information.
            </p>
          ),
        },
        {
          id: "choice-marketing",
          title: "Marketing choices",
          content: (
            <p>
              Use the unsubscribe link in a marketing message or email us to opt
              out. We may continue to send non-marketing messages that are needed
              for an order, security, return, policy update, or service request.
            </p>
          ),
        },
        {
          id: "choice-cookies",
          title: "Cookies and browser storage",
          content: (
            <p>
              This storefront currently uses essential local browser storage for
              the bag and wish list. You can remove it through browser controls,
              which may clear those saved selections. See the <Link to="/cookie-policy">Cookie Policy</Link>.
            </p>
          ),
        },
        {
          id: "choice-sale",
          title: "Sale or sharing of personal information",
          content: (
            <p>
              We do not sell personal information for money and this storefront is
              not configured for cross-context behavioral advertising. If those
              practices change, we will update our notices and provide any legally
              required opt-out method before using information that way.
            </p>
          ),
        },
        {
          id: "choice-appeal",
          title: "Response, appeal, and complaints",
          content: (
            <>
              <p>
                We respond within the period required by applicable law and explain
                any extension or refusal. Where a right to appeal applies, reply to
                our decision with the subject "Privacy Appeal." You may also
                complain to your local data-protection authority.
              </p>
              <PolicyContact label="Privacy requests" />
            </>
          ),
        },
      ]}
    />
  );
}

export function AccessibilityStatement() {
  return (
    <PolicyPage
      title="Accessibility statement"
      introduction={`${siteConfig.brandName} wants customers with different abilities and technologies to be able to browse, understand, and shop on this website.`}
      sections={[
        {
          id: "access-commitment",
          title: "Our approach",
          content: (
            <p>
              We consider keyboard access, visible focus, readable contrast,
              semantic structure, alternative text, form labels, reduced motion,
              and responsive layouts as part of ongoing design and maintenance.
              This statement is not a certification of complete conformity with a
              particular technical standard.
            </p>
          ),
        },
        {
          id: "access-features",
          title: "Accessibility features",
          content: (
            <ul>
              <li>Keyboard-visible focus and controls with descriptive labels.</li>
              <li>Headings, landmarks, and link text intended to support navigation.</li>
              <li>Layouts that adapt to smaller screens and browser zoom.</li>
              <li>Support for reduced-motion preferences where animation is present.</li>
            </ul>
          ),
        },
        {
          id: "access-compatibility",
          title: "Technology and compatibility",
          content: (
            <p>
              For the best experience, use a current browser and operating system
              with accessibility features enabled. Older or unsupported technology
              may not interpret newer web standards consistently.
            </p>
          ),
        },
        {
          id: "access-limitations",
          title: "Known limitations",
          content: (
            <p>
              Third-party checkout, wallet, carrier, or embedded services may have
              accessibility behavior outside our direct control. Product imagery
              may also communicate visual detail that is difficult to capture fully
              in brief alternative text. We welcome reports of any specific barrier.
            </p>
          ),
        },
        {
          id: "access-assistance",
          title: "Alternative assistance",
          content: (
            <p>
              If you cannot access product, order, delivery, return, or policy
              information, email us. We will make reasonable efforts to provide the
              information or service through an accessible alternative.
            </p>
          ),
        },
        {
          id: "access-feedback",
          title: "Feedback",
          content: (
            <>
              <p>
                Include the page address, a short description of the barrier, your
                browser or assistive technology if you are comfortable sharing it,
                and the format or support you need.
              </p>
              <PolicyContact label="Accessibility feedback" />
            </>
          ),
        },
      ]}
    />
  );
}

export function AcceptableUse() {
  return (
    <PolicyPage
      title="Acceptable use policy"
      introduction="Rules that protect customers, services, content, and systems when this website is used."
      sections={[
        {
          id: "use-permitted",
          title: "Permitted use",
          content: (
            <p>
              You may browse, create a personal shopping selection, contact Client
              Services, and place genuine retail orders for lawful personal use.
              Any access must also comply with our <Link to="/terms">Terms and Conditions</Link>.
            </p>
          ),
        },
        {
          id: "use-prohibited",
          title: "Prohibited conduct",
          content: (
            <ul>
              <li>Breaking a law, infringing another person's rights, or facilitating fraud.</li>
              <li>Impersonating a person or using false payment, identity, delivery, or contact details.</li>
              <li>Introducing malware or attempting to bypass authentication, rate limits, or security controls.</li>
              <li>Interfering with availability or placing a disproportionate load on the service.</li>
              <li>Scraping, crawling, or using bots to copy content, reserve inventory, or automate purchases without written permission.</li>
            </ul>
          ),
        },
        {
          id: "use-content",
          title: "Content and data",
          content: (
            <p>
              You must not copy, republish, train a commercial system on, frame, or
              commercially exploit site content except with permission or where law
              expressly allows it. You must not collect another customer's personal
              information through the site.
            </p>
          ),
        },
        {
          id: "use-security",
          title: "Security research",
          content: (
            <p>
              Do not test, scan, or exploit the website without prior written
              authorization. If you believe you found a vulnerability, stop any
              activity that could access data or disrupt service and report it
              privately to our contact email.
            </p>
          ),
        },
        {
          id: "use-linking",
          title: "Links to this website",
          content: (
            <p>
              Fair, lawful links that do not imply endorsement are permitted. Do not
              frame the site, obscure its source, use our marks as misleading link
              text, or link from material that is unlawful or infringing.
            </p>
          ),
        },
        {
          id: "use-enforcement",
          title: "Enforcement",
          content: (
            <p>
              We may limit or suspend access, cancel affected orders, preserve
              evidence, and notify service providers or authorities where reasonably
              necessary and lawful. Enforcement is proportionate to the conduct and
              does not limit other rights or remedies.
            </p>
          ),
        },
        {
          id: "use-report",
          title: "Report misuse",
          content: <PolicyContact label="Misuse and security reports" />,
        },
      ]}
    />
  );
}

export function IntellectualProperty() {
  return (
    <PolicyPage
      title="Intellectual property notice"
      introduction="This notice explains permitted use of website materials and how to report content that may infringe intellectual-property rights."
      sections={[
        {
          id: "ip-ownership",
          title: "Ownership and licences",
          content: (
            <p>
              Unless otherwise identified, the website's text, graphics, product
              presentation, photography, code, layout, and other materials are
              owned by or lawfully licensed to {siteConfig.brandName}. They are
              protected by copyright, trademark, design, and other applicable laws.
            </p>
          ),
        },
        {
          id: "ip-personal-use",
          title: "Limited personal use",
          content: (
            <p>
              You may view the site and make temporary copies that your browser
              requires for personal shopping. No ownership right is transferred.
              Reproduction, adaptation, distribution, sale, or commercial use
              requires written permission unless an applicable legal exception
              expressly permits it.
            </p>
          ),
        },
        {
          id: "ip-marks",
          title: "Names, marks, and domains",
          content: (
            <p>
              The {siteConfig.brandName} name, wordmark, domain, and related source
              identifiers may not be used to suggest sponsorship, affiliation, or
              authenticity without permission. Third-party names and payment marks
              remain the property of their respective owners.
            </p>
          ),
        },
        {
          id: "ip-submissions",
          title: "Materials you submit",
          content: (
            <p>
              Do not send content you are not entitled to share. When you send
              photos or documents to resolve an order or product issue, you allow us
              to use them only as reasonably necessary to review, document, and
              resolve that request or meet legal obligations.
            </p>
          ),
        },
        {
          id: "ip-report",
          title: "Reporting alleged infringement",
          content: (
            <>
              <p>A report should include:</p>
              <ul>
                <li>Your name, contact details, and authority to act for the rights holder.</li>
                <li>A description of the protected work, mark, or design.</li>
                <li>The exact website address and description of the material at issue.</li>
                <li>A good-faith explanation of why the use is unauthorized.</li>
                <li>A statement that the information supplied is accurate.</li>
              </ul>
              <p>
                Knowingly false reports may cause harm and legal liability. We may
                request additional information and may forward the report to the
                content provider where appropriate.
              </p>
            </>
          ),
        },
        {
          id: "ip-response",
          title: "Review and response",
          content: (
            <>
              <p>
                We assess complete reports in good faith and may remove, restrict,
                or restore material as appropriate. This email contact is not a
                representation that it is a formally registered statutory agent in
                every jurisdiction.
              </p>
              <PolicyContact label="Intellectual-property reports" />
            </>
          ),
        },
      ]}
    />
  );
}

export function ConsumerRights() {
  return (
    <PolicyPage
      title="Consumer rights notice"
      introduction="A plain-language overview of mandatory consumer protections. It supplements our sales terms and does not replace rights that apply by law."
      sections={[
        {
          id: "consumer-mandatory",
          title: "Mandatory rights remain in place",
          content: (
            <p>
              Nothing in our policies excludes, restricts, or changes a consumer
              guarantee, warranty, remedy, cancellation right, or liability that
              cannot lawfully be excluded or restricted in your place of residence.
            </p>
          ),
        },
        {
          id: "consumer-conformity",
          title: "Conforming goods",
          content: (
            <p>
              Applicable law may require goods to match their description, be of
              satisfactory or acceptable quality, be fit for disclosed purposes,
              and remain free from certain defects for a reasonable period. The
              exact tests and time limits vary by jurisdiction.
            </p>
          ),
        },
        {
          id: "consumer-remedies",
          title: "Remedies",
          content: (
            <p>
              If a mandatory guarantee is not met, available remedies may include
              repair, replacement, price reduction, cancellation, or refund. The
              remedy may depend on the seriousness of the problem, the time since
              delivery, and local law.
            </p>
          ),
        },
        {
          id: "consumer-change-mind",
          title: "Change-of-mind returns and cancellation rights",
          content: (
            <p>
              Our <Link to="/returns">Returns Policy</Link> provides a contractual
              return window for eligible items. Separate statutory cooling-off or
              cancellation rights may apply to distance sales in some locations,
              with their own deadlines, exclusions, and notice requirements.
            </p>
          ),
        },
        {
          id: "consumer-complaints",
          title: "How to make a complaint",
          content: (
            <p>
              Email the order number, a clear description of the issue, the outcome
              you are seeking, and relevant photographs or records. We will review
              the complaint fairly and request only information reasonably needed
              to investigate it.
            </p>
          ),
        },
        {
          id: "consumer-escalation",
          title: "Escalation",
          content: (
            <p>
              If you disagree with our response, ask for a further review. You may
              also be entitled to contact a consumer-protection agency, payment
              provider, court, tribunal, or approved dispute-resolution body. We do
              not claim participation in a particular voluntary scheme unless we
              identify it expressly.
            </p>
          ),
        },
        {
          id: "consumer-contact",
          title: "Contact",
          content: <PolicyContact label="Consumer complaints" />,
        },
      ]}
    />
  );
}

export function FraudPrevention() {
  return (
    <PolicyPage
      title="Fraud prevention policy"
      introduction="How proportionate checks may be used to protect customers, payments, inventory, and the storefront from unauthorized activity."
      sections={[
        {
          id: "fraud-purpose",
          title: "Why checks are used",
          content: (
            <p>
              Fraud controls help detect stolen payment methods, account takeover,
              false identities, abusive returns, unauthorized resale, and automated
              purchasing. They also help us meet legal, payment-network, and
              accounting obligations.
            </p>
          ),
        },
        {
          id: "fraud-data",
          title: "Information considered",
          content: (
            <p>
              Checks may consider order and account details, payment results,
              delivery information, device and network signals, order history, and
              inconsistencies suggesting unauthorized activity. Complete card
              credentials are handled by the payment provider.
            </p>
          ),
        },
        {
          id: "fraud-actions",
          title: "Possible actions",
          content: (
            <p>
              We may request limited verification, delay fulfilment while reviewing
              an order, apply quantity limits, decline payment, cancel an order, or
              suspend abusive access. We do not ask customers to send passwords,
              one-time codes, or full card data by email.
            </p>
          ),
        },
        {
          id: "fraud-sharing",
          title: "Service providers and disclosure",
          content: (
            <p>
              Necessary information may be shared with payment processors, fraud
              prevention providers, carriers, insurers, advisers, and authorities
              as permitted by law. These recipients receive only information
              appropriate to their role or lawful request.
            </p>
          ),
        },
        {
          id: "fraud-decisions",
          title: "Reviewing a decision",
          content: (
            <p>
              Automated signals may inform a review, but a customer may contact us
              if an order is declined or held. We may not be able to disclose
              security rules that would make evasion easier, but we will recheck
              available information where appropriate.
            </p>
          ),
        },
        {
          id: "fraud-report",
          title: "Reporting suspected fraud",
          content: (
            <>
              <p>
                If you receive an unexpected order message or see a payment you do
                not recognize, contact your financial institution immediately, then
                email us. Preserve relevant messages and do not click suspicious
                links or share verification codes.
              </p>
              <PolicyContact label="Fraud reports" />
            </>
          ),
        },
      ]}
    />
  );
}

export function ElectronicCommunications() {
  return (
    <PolicyPage
      title="Electronic communications policy"
      introduction="How order, service, legal, and optional marketing messages are delivered electronically."
      sections={[
        {
          id: "communications-scope",
          title: "Electronic delivery",
          content: (
            <p>
              When you place an order or contact us, you agree that confirmations,
              receipts, delivery updates, return messages, security notices, and
              responses may be sent electronically to the contact details you
              provide, where permitted by law.
            </p>
          ),
        },
        {
          id: "communications-requirements",
          title: "Technical requirements",
          content: (
            <p>
              You need a current browser, an active email address, and software
              capable of displaying ordinary web pages and email. You may save or
              print messages for your records. Contact us if you need an accessible
              alternative format.
            </p>
          ),
        },
        {
          id: "communications-accuracy",
          title: "Keeping contact details current",
          content: (
            <p>
              Check your email address carefully at checkout and monitor filtered or
              spam folders. Tell us promptly if order contact details are incorrect.
              We cannot guarantee that an address can be changed after preparation
              has begun.
            </p>
          ),
        },
        {
          id: "communications-marketing",
          title: "Marketing messages",
          content: (
            <p>
              Marketing is optional and is sent only on an appropriate legal basis.
              You can unsubscribe at any time. Opting out of marketing does not stop
              transactional or service messages needed to complete an order or
              respond to a request.
            </p>
          ),
        },
        {
          id: "communications-withdrawal",
          title: "Withdrawing electronic consent",
          content: (
            <p>
              Where the law gives you a right to withdraw consent to electronic
              delivery, email us. Withdrawal may mean that we cannot continue an
              online transaction that requires electronic records, but it will not
              affect communications already delivered.
            </p>
          ),
        },
        {
          id: "communications-safety",
          title: "Message safety",
          content: (
            <>
              <p>
                We will not ask by ordinary email for your password, full payment
                card number, card security code, or one-time authentication code.
                Confirm suspicious messages through the contact address published
                on this website.
              </p>
              <PolicyContact label="Communication enquiries" />
            </>
          ),
        },
      ]}
    />
  );
}

export function LegalNotice() {
  return (
    <PolicyPage
      title="Legal notice and contact"
      introduction="Core website, contracting, and customer-service information for this online storefront."
      sections={[
        {
          id: "notice-operator",
          title: "Website identity",
          content: (
            <p>
              This storefront operates under the brand name {siteConfig.brandName}.
              The customer-service and policy contact published for the site is
              <a href={`mailto:${siteConfig.contact.email}`}> {siteConfig.contact.email}</a>.
              Seller information applicable to a purchase is provided in the
              checkout or order confirmation where required.
            </p>
          ),
        },
        {
          id: "notice-contract",
          title: "Contract information",
          content: (
            <p>
              Product characteristics, price, currency, delivery charges, taxes,
              payment method, and the final total are displayed before order
              submission. The order acknowledgement and shipping confirmation form
              the electronic record of the transaction together with the policies
              linked at checkout.
            </p>
          ),
        },
        {
          id: "notice-language",
          title: "Language and records",
          content: (
            <p>
              The storefront and online contract are offered in English. Save the
              order confirmation and current policy pages for your records. We
              retain transaction records only as described in the Privacy Policy
              and as required for business or legal purposes.
            </p>
          ),
        },
        {
          id: "notice-third-party",
          title: "Third-party services",
          content: (
            <p>
              Payment, wallet, hosting, and delivery services may be supplied by
              independent providers under their own terms and privacy notices. A
              reference or link does not transfer responsibility for an independent
              service beyond what applicable law requires.
            </p>
          ),
        },
        {
          id: "notice-no-affiliation",
          title: "Brand independence",
          content: (
            <p>
              {siteConfig.brandName} is an independent brand. References to
              third-party payment methods, carriers, technologies, or trademarks
              identify compatibility or service providers and do not imply an
              endorsement beyond the relevant service relationship.
            </p>
          ),
        },
        {
          id: "notice-complaints",
          title: "Questions and complaints",
          content: (
            <>
              <p>
                Include your order number and a concise description of the issue.
                Never email passwords or complete card credentials. For privacy,
                accessibility, or intellectual-property matters, name that topic in
                the subject line so the request can be routed correctly.
              </p>
              <PolicyContact label="Official website contact" />
            </>
          ),
        },
      ]}
    />
  );
}
